import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {IRootState} from 'app/shared/reducers';
import {Button, ButtonGroup, Col, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row} from 'reactstrap';
import {AvField, AvForm, AvGroup, AvInput} from 'availity-reactstrap-validation';
import {IDriver} from 'app/shared/model/driver.model';
import {IRegion} from 'app/shared/model/region.model';
import {createEntity as createDriver, reset} from 'app/entities/driver/driver.reducer';
import {getVillagesCompact} from 'app/entities/village/village.reducer';
import {getAllByType as getCountries} from 'app/entities/region/region.reducer';
import {getEntities as getVehicleTypes} from 'app/entities/vehicleType/vehicleType.reducer';
import {getHealthcareFacilitiesCompact} from 'app/entities/healthcare-facility/healthcare-facility.reducer';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {hasAnyPermission} from 'app/shared/auth/permissions';
import VillageAvTypeahead from 'app/shared/layout/menus/village-av-typeahead';
import { useRegionLabels } from 'app/shared/hooks/useRegionLabels';
import { RegionDescriptionType } from 'app/shared/model/enumerations/region-description-type.model';

const neighborVillageSectionStyle = {
  border: '5px solid #e0e0e0',
  padding: '1em',
  marginBottom: '1em'
};

const neighborVillageEntryRowStyle = {
  display: 'flex',
  justifyContent: 'space-between'
};

const neighborVillageRemoveButtonStyle = {
  width: '10em',
  margin: '0.3em 0.3em 0.3em 1em',
  paddingLeft: '1em',
  fontSize: 'smaller'
};

const radios = [
  {name: 'Active', value: true},
  {name: 'Disabled', value: false}
];

interface AddDriverModalProps extends StateProps, DispatchProps {
  isOpen: boolean;
  toggle: () => void;
  onSave: (driver: IDriver) => void;
  countryId?: string;
}

const AddDriverModalComponent = (props: AddDriverModalProps) => {
  const {isOpen, toggle, onSave, countryId, countries} = props;
  const { getLabel } = useRegionLabels();
  const {villages, loading, updating, villagesLoading, vehicleTypes, createSuccess, createdDriver} = props;

  const [activeCountry, setActiveCountry] = useState<IRegion>({});
  const [neighborVillageIds, setNeighborVillageIds] = useState([]);
  const [neighborHfIds, setNeighborHfIds] = useState([]);
  const [canAddNeighborVillage, setCanAddNeighborVillage] = useState(true);
  const [canAddNeighborHf, setCanAddNeighborHf] = useState(true);
  const [activeStatus, setActiveStatus] = useState(true);
  const [vehicleTypeList, setVehicleTypeList] = useState([]);

  let {healthcareFacilities} = props;
  healthcareFacilities = activeCountry ? healthcareFacilities.filter(el => el.countryId === activeCountry.id) : [];

  const getHfName = (id, hfList) => {
    const hf = hfList.find(v => v.id === id);
    return !hf ?
      {short: '', full: '', council: '', district: ''}
      :
      {
        short: hf.name,
        council: hf?.councilName,
        district: hf?.districtName,
        full: (hf.name + ' / ' + hf?.councilName + ' / ' + hf?.districtName)
      };
  };

  const hfNames = healthcareFacilities ?
    healthcareFacilities.map(hf => ({
      short: hf.name,
      council: hf.councilName,
      district: hf.districtName,
      full: (hf.name + ' / ' + hf.councilName + ' / ' + hf.districtName),
      id: hf.id
    })) : [];

  const getVillageNameCompact = (id, villageList) => {
    const village = villageList.find(v => v.id === id);
    return !village ?
      {short: '', full: '', council: '', district: '', latitude: 0, longitude: 0, activated: ""}
      :
      {
        short: village.name,
        council: village.councilName,
        district: village.districtName,
        latitude: village.locationLat,
        longitude: village.locationLon,
        activated: village.activated,
        full: (village.name + ' / ' + village.councilName + ' / ' + village.districtName)
      };
  };

  const villageNames = villages ?
    villages.map(village => ({
      short: village.name,
      council: village.councilName,
      district: village.districtName,
      latitude: village.locationLat,
      longitude: village.locationLon,
      activated: village.activated,
      full: (village.name + ' / ' + village.councilName + ' / ' + village.districtName),
      id: village.id
    })) : [];

  useEffect(() => {
    if (isOpen) {
      props.reset();
      if (!countries || countries.length === 0) {
        props.getCountries();
      }

      if (!vehicleTypes || vehicleTypes.length === 0) {
        props.getVehicleTypes();
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (countries && countries.length > 0 && isOpen) {
      const ac = countries.find(country =>
        country.id === (countryId || (props.isAdmin ? countries[0].id : props.userCountryId))
      );
      if (ac) {
        setActiveCountry(ac);
        if (!villages || villages.length === 0 || villages[0]?.countryId !== ac.id) {
          props.getVillages(ac.id);
        }
        if (!healthcareFacilities || healthcareFacilities.length === 0 || healthcareFacilities[0]?.countryId !== ac.id) {
          props.getFacilities(ac.id);
        }
      }
    }
  }, [countries, countryId, isOpen]);

  useEffect(() => {
    if (vehicleTypes?.length > 0 && activeCountry.id && isOpen) {
      const filteredTypes = vehicleTypes.filter((item: any) => item.countryId === activeCountry.id);
      setVehicleTypeList(filteredTypes);
    }
  }, [vehicleTypes, activeCountry, isOpen]);

  useEffect(() => {
    if (createSuccess && createdDriver) {
      onSave(createdDriver);
      toggle();
      // Reset form
      setNeighborVillageIds([]);
      setNeighborHfIds([]);
      setActiveStatus(true);
    }
  }, [createSuccess, createdDriver]);

  useEffect(() => {
    setCanAddNeighborVillage((neighborVillageIds.length === 0) || neighborVillageIds[neighborVillageIds.length - 1]);
  }, [neighborVillageIds]);

  useEffect(() => {
    setCanAddNeighborHf((neighborHfIds.length === 0) || neighborHfIds[neighborHfIds.length - 1]);
  }, [neighborHfIds]);

  const saveEntity = (event, errors, values) => {
    if (errors.length === 0) {
      const entity = {
        ...values,
        activated: activeStatus,
        neighborVillageIds,
        neighborHealthcareFacilityIds: neighborHfIds,
        vehicleType: vehicleTypes.find(item => item.id === values.vehicleType),
        driverType: 'CAR',
        countryId: activeCountry.id
      };

      props.createDriver(entity);
    }
  };

  const addNeighborVillage = () => {
    setNeighborVillageIds(neighborVillageIds.concat(undefined));
  };

  const addNeighborHf = () => {
    setNeighborHfIds(neighborHfIds.concat(undefined));
  };

  const removeNeighborVillage = (i) => {
    const newNeighborVillageIds = neighborVillageIds.slice();
    newNeighborVillageIds.splice(i, 1);
    setNeighborVillageIds(newNeighborVillageIds);
  };

  const removeNeighborHf = (i) => {
    const newNeighborHfIds = neighborHfIds.slice();
    newNeighborHfIds.splice(i, 1);
    setNeighborHfIds(newNeighborHfIds);
  };

  const neighborVillageChange = (changedIndex, newValue) => {
    const newNeighborVillageIds = neighborVillageIds.map((val, index) => {
      return (index === changedIndex) ? newValue : val;
    });
    setNeighborVillageIds(newNeighborVillageIds);
  };

  const neighborHfChange = (changedIndex, newValue) => {
    const newNeighborHfIds = neighborHfIds.map((val, index) => {
      return (index === changedIndex) ? newValue : val;
    });
    setNeighborHfIds(newNeighborHfIds);
  };

  const villageOptionList = (includeEmpty = false) => {
    let result = (includeEmpty) ? ([{short: '', full: '', id: undefined}]) : [];
    result = result.concat(villageNames);
    return result;
  };

  const hfOptionList = (includeEmpty = false) => {
    let result = (includeEmpty) ? ([{short: '', full: '', id: undefined}]) : [];
    result = result.concat(hfNames);
    return result;
  };

  const neighborVillageFields = () => {
    const result = [];
    neighborVillageIds.forEach((villageId, i) => {
      result.push(
        <div key={`neighbor-village-${i}`} style={neighborVillageEntryRowStyle}>
          <div style={{flex: '1'}}>
            <VillageAvTypeahead
              onChange={(value) => neighborVillageChange(i, value)}
              labelKey="name"
              valueKey="id"
              data={villageOptionList(!villageId)}
              placeholder={!villageId ? '' : getVillageNameCompact(villageId, villages).full}
              id={'neighbor-village-' + i + '-typeahead'}
              loading={villagesLoading}
              value={villageId || ''}
              input={{
                id: 'neighbor-village-' + i,
                name: 'formNeighborVillageValues[' + i + ']',
                required: true
              }}
            />
          </div>
          <Button style={neighborVillageRemoveButtonStyle} color="danger"
                  onClick={() => removeNeighborVillage(i)}>
            <FontAwesomeIcon icon="trash"/>
            &nbsp; Remove
          </Button>
        </div>
      );
    });
    return result;
  };

  const neighborHfFields = () => {
    const result = [];
    neighborHfIds.forEach((hfId, i) => {
      result.push(
        <div key={`neighbor-hf-${i}`} style={neighborVillageEntryRowStyle}>
          <div style={{flex: '1'}}>
            <VillageAvTypeahead
              onChange={(value) => neighborHfChange(i, value)}
              labelKey="name"
              valueKey="id"
              data={hfOptionList(!hfId)}
              placeholder={!hfId ? '' : getHfName(hfId, healthcareFacilities).full}
              id={'neighbor-hf-' + i + '-typeahead'}
              loading={false}
              value={hfId || ''}
              input={{
                id: 'neighbor-hf-' + i,
                name: 'formNeighborHfValues[' + i + ']',
                required: true
              }}
            />
          </div>
          <Button style={neighborVillageRemoveButtonStyle} color="danger"
                  onClick={() => removeNeighborHf(i)}>
            <FontAwesomeIcon icon="trash"/>
            &nbsp; Remove
          </Button>
        </div>
      );
    });
    return result;
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        Add New Driver
      </ModalHeader>
      <ModalBody>
        {loading ? (
          <p>Loading...</p>
        ) : (
          <AvForm id="driver-form" onSubmit={saveEntity}>
            <AvGroup>
              <Label id="activeStatusLabel">
                Active Status
              </Label>
              <ButtonGroup style={{marginLeft: "30px"}}>
                {radios.map(radio => (
                  <Button
                    key={radio.value.toString()}
                    color={activeStatus === radio.value ? "primary" : "secondary"}
                    onClick={() => setActiveStatus(radio.value)}
                    active={activeStatus === radio.value}
                  >
                    {radio.name}
                  </Button>
                ))}
              </ButtonGroup>
            </AvGroup>

            {countries && countries.length > 0 && (
              <AvGroup>
                <Label for="driver-country">Country</Label>
                <AvInput
                  id="driver-country"
                  type="select"
                  className="form-control"
                  name="country"
                  value={activeCountry?.id || ''}
                  disabled
                >
                  <option value="">Select a country</option>
                  {countries.map(country => (
                    <option key={country.id} value={country.id}>
                      {country.name}
                    </option>
                  ))}
                </AvInput>
              </AvGroup>
            )}

            <AvGroup>
              <Label id="firstNameLabel" for="driver-firstName">
                First Name
              </Label>
              <AvField
                id="driver-firstName"
                type="text"
                name="firstName"
                validate={{
                  required: {value: true, errorMessage: 'This field is required.'},
                }}
              />
            </AvGroup>

            <AvGroup>
              <Label id="lastNameLabel" for="driver-lastName">
                Last Name
              </Label>
              <AvField
                id="driver-lastName"
                type="text"
                name="lastName"
                validate={{
                  required: {value: true, errorMessage: 'This field is required.'},
                }}
              />
            </AvGroup>

            <AvGroup>
              <Label id="phoneNumberLabel" for="driver-phoneNumber">
                Phone Number
              </Label>
              <AvField
                id="driver-phoneNumber"
                type="text"
                name="phoneNumber"
                validate={{
                  required: {value: true, errorMessage: 'This field is required.'},
                  maxLength: {value: 15, errorMessage: 'Maximum length is 15 characters'}
                }}
              />
            </AvGroup>

            <AvGroup>
              <Label id="phone2Label" for="driver-phone2">
                Phone Number 2
              </Label>
              <AvField
                id="driver-phone2"
                type="text"
                name="phone_number_2"
                validate={{
                  maxLength: {value: 15, errorMessage: 'Maximum length is 15 characters'}
                }}
              />
            </AvGroup>

            <AvGroup>
              <Label id="homeVillageLabel" for="driver-homeVillage">
                Home {getLabel(RegionDescriptionType.VILLAGE)}
              </Label>
              <VillageAvTypeahead
                labelKey="name"
                valueKey="id"
                data={villageOptionList(false)}
                placeholder={`Select Home ${getLabel(RegionDescriptionType.VILLAGE)}`}
                id="driver-homeVillage"
                loading={villagesLoading}
                input={{
                  id: "driver-homeVillage",
                  name: "homeVillageId",
                  required: true
                }}
              />
            </AvGroup>

            <AvGroup>
              <Label id="vehicleTypeLabel" for="driver-vehicleType">
                Vehicle Type
              </Label>
              <AvInput
                id="driver-vehicleType"
                type="select"
                className="form-control"
                name="vehicleType"
                required
              >
                <option value="">Select a vehicle type</option>
                {vehicleTypeList.map(vt => (
                  <option key={vt.id} value={vt.id}>
                    {vt.enType}
                  </option>
                ))}
              </AvInput>
            </AvGroup>

            <AvGroup>
              <Label id="mpesaNumberLabel" for="driver-mpesaNumber">
                Mobile Money ID
              </Label>
              <AvField
                id="driver-mpesaNumber"
                type="text"
                name="mpesaNumber"
                validate={{
                  maxLength: {value: 10, errorMessage: 'Maximum length is 10 digits'},
                  number: {value: true, errorMessage: 'This field should be a number.'},
                }}
              />
            </AvGroup>

            {/* Neighbor Villages Section */}
            <div style={neighborVillageSectionStyle}>
              <div style={{marginBottom: '0.75em', display: 'flex', justifyContent: 'space-between'}}>
                <Label>Neighbor {getLabel(RegionDescriptionType.VILLAGE)}</Label>
                <Button
                  color="primary"
                  onClick={addNeighborVillage}
                  disabled={!canAddNeighborVillage}
                  style={{fontSize: 'smaller'}}
                >
                  <FontAwesomeIcon icon="plus"/> Add {getLabel(RegionDescriptionType.VILLAGE)}
                </Button>
              </div>
              {neighborVillageFields()}
            </div>

            {/* Neighbor Healthcare Facilities Section */}
            <div style={neighborVillageSectionStyle}>
              <div style={{marginBottom: '0.75em', display: 'flex', justifyContent: 'space-between'}}>
                <Label>Neighbor Healthcare Facilities</Label>
                <Button
                  color="primary"
                  onClick={addNeighborHf}
                  disabled={!canAddNeighborHf}
                  style={{fontSize: 'smaller'}}
                >
                  <FontAwesomeIcon icon="plus"/> Add Healthcare Facility
                </Button>
              </div>
              {neighborHfFields()}
            </div>
          </AvForm>
        )}
      </ModalBody>

      <ModalFooter className="d-block">
        <Row className="mr-0 ml-0">
          <Col md="6" className="text-left">
            <Button color="secondary" onClick={toggle}>
              <FontAwesomeIcon icon="times"/> Cancel
            </Button>
          </Col>
          <Col md="6" className="text-right">
            <Button
              color="primary"
              type="submit"
              disabled={updating}
              form="driver-form"
            >
              <FontAwesomeIcon icon="save"/> Save
            </Button>
          </Col>
        </Row>
      </ModalFooter>
    </Modal>
  );
};

const mapStateToProps = (storeState: IRootState) => ({
  villages: storeState.village.compactEntities,
  villagesLoading: storeState.village.compactLoading,
  loading: storeState.driver.loading,
  updating: storeState.driver.updating,
  createSuccess: storeState.driver.updateSuccess,
  createdDriver: storeState.driver.entity,
  healthcareFacilities: storeState.healthcareFacility.compactEntities,
  countries: storeState.region.countries,
  vehicleTypes: storeState.vehicleType.entities,
  userCountryId: storeState.authentication.userCountryId,
  isAdmin: hasAnyPermission(storeState.authentication.account.roles, ['ADMIN'])
});

const mapDispatchToProps = {
  getVillages: getVillagesCompact,
  createDriver,
  reset,
  getFacilities: getHealthcareFacilitiesCompact,
  getCountries,
  getVehicleTypes
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

const AddDriverModal = connect(
  mapStateToProps,
  mapDispatchToProps
)(AddDriverModalComponent);

export default AddDriverModal;
